import React, { memo, useEffect, useRef } from 'react'

import { arrayOf, bool, func, number, oneOf, shape, string } from 'prop-types'

import { TURN_STATUS } from 'Pods/ThreeDStudio/constants'
import useTranslation from 'Pods/ThreeDStudio/Hooks/useTranslation'

import FailedState from './Components/FailedState/FailedState'
import GeneratingState from './Components/GeneratingState/GeneratingState'
import Result from './Components/Result/Result'
import SentMessage from './Components/SentMessage/SentMessage'

function Conversation({ messages, isGenerating, onApply, onRegenerate, onGenerateAgain, onEdit }) {
  const t = useTranslation()
  const bottomRef = useRef(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' })
  }, [messages])

  if (messages.length === 0) {
    return (
      <div className="image-assistant-create-tab__create-empty">
        <p className="image-assistant-create-tab__create-empty-text cgt-heading-50">
          {t('imageAssistantPanel.createTab.conversation.emptyPrompt')}
        </p>
      </div>
    )
  }

  return (
    <div className="image-assistant-create-tab__conversation">
      {messages.map(message => (
        <div key={message.id} className="image-assistant-create-tab__turn">
          {!message.continuation && <SentMessage prompt={message.prompt} images={message.images} />}

          {message.status === TURN_STATUS.LOADING && <GeneratingState />}

          {message.status === TURN_STATUS.FAILED && (
            <FailedState onGenerateAgain={() => onGenerateAgain(message)} />
          )}

          {message.status === TURN_STATUS.DONE && (
            <Result
              result={message.result}
              isGenerating={isGenerating}
              onApply={() => onApply(message.result)}
              onRegenerate={() => onRegenerate(message)}
              onEdit={() => onEdit(message.result)}
            />
          )}
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  )
}

Conversation.propTypes = {
  messages: arrayOf(shape({
    id: number.isRequired,
    prompt: string,
    images: arrayOf(shape({ id: number, url: string })).isRequired,
    status: oneOf([TURN_STATUS.LOADING, TURN_STATUS.DONE, TURN_STATUS.FAILED]).isRequired,
    result: shape({ id: number, url: string, alt: string }),
    continuation: bool,
  })).isRequired,
  isGenerating: bool.isRequired,
  onApply: func.isRequired,
  onRegenerate: func.isRequired,
  onGenerateAgain: func.isRequired,
  onEdit: func.isRequired,
}

export default memo(Conversation)
